import { useEffect, useRef } from 'react';
import type { AppUpdateStatus, Bridge, HostInfo } from '@casa/bridge';
import { appLog } from '../diagnostics/log.ts';

// The page's part in the shell's updates of the app (docs/specs/app-updates.md AU-25 to AU-27): it
// says it started (so a new release is kept, AU-26), lets a downloaded release take over at a quiet
// moment (the owner, QAU-1: no touch for 10 minutes, not editing, no dialog or settings open; or the
// app coming back to the foreground), and says once that it was updated. In a browser, and in a
// shell without updates, none of it does anything.

/** No touch or key for this long, on a page with nothing open, is a quiet moment (QAU-1). */
export const QUIET_MS = 10 * 60_000;
/** How often the page asks the shell whether a release is ready. */
const STATUS_EVERY_MS = 5 * 60_000;
const SEEN_VERSION_KEY = 'casa.updates.seenVersion';

export interface PageState {
    /** When someone last touched the screen or pressed a key (ms). */
    lastActivity: number;
    editing: boolean;
    /** The settings, a dialog or a full-screen view are open over the dashboard. */
    somethingOpen: boolean;
}

/** Whether a pending release may take over now: the page would be replaced under nobody's hands. */
export function isQuietMoment(now: number, page: PageState) : boolean {
    return !page.editing && !page.somethingOpen && now - page.lastActivity >= QUIET_MS;
}

/** "Updated to 0.27.0", once per release downloaded, after it took over; undefined otherwise. */
export function updatedNotice(status: AppUpdateStatus, seenVersion: string | undefined) : string | undefined {
    return status.active.source === 'downloaded' && seenVersion !== undefined && seenVersion !== status.active.version ? `Updated to ${status.active.version}.` : undefined;
}

/** Whether something lies over the dashboard: the settings, a dialog, a full-screen view. From the page itself. */
function isSomethingOpen() : boolean {
    return document.querySelector('[data-testid="settings"], [role="dialog"], [role="alertdialog"]') !== null;
}

function readSeen() : string | undefined {
    try {
        return localStorage.getItem(SEEN_VERSION_KEY) ?? undefined;
    } catch {
        return undefined;
    }
}

function writeSeen(version: string) : void {
    try {
        localStorage.setItem(SEEN_VERSION_KEY, version);
    } catch {
        // Without storage the notice may come again, or not at all; nothing else depends on it.
    }
}

/** A shell with updates: bridge level 1 and above (AU-5). Not a browser, which the Tilez Hub serves. */
function shellHasUpdates() : boolean {
    return ((globalThis as { casaHost?: HostInfo }).casaHost?.bridgeLevel ?? 0) >= 1;
}

/** Runs the page's part of the updates for the dashboard; `notify` shows a short notice. */
export function useAppUpdates(bridge: Bridge | null, editing: boolean, notify: (text: string) => void) : void {
    const lastActivity = useRef(Date.now());
    const isEditing = useRef(editing);
    isEditing.current = editing;

    useEffect(() => {
        if (!bridge || !shellHasUpdates()) {
            return undefined;
        }

        const touched = () : void => {
            lastActivity.current = Date.now();
        };
        window.addEventListener('pointerdown', touched, { capture: true, passive: true });
        window.addEventListener('keydown', touched, { capture: true, passive: true });

        const applyWhen = async (moment: string, isMoment: () => boolean) : Promise<void> => {
            const status = await bridge.app.updateStatus().catch(() => undefined);
            if (status?.pending && isMoment()) {
                appLog.write('info', `updates: ${status.pending.version} (build ${status.pending.build}) takes over: ${moment}`);
                await bridge.app.applyUpdate().catch(() => undefined);
            }
        };
        const quiet = () : boolean => isQuietMoment(Date.now(), { lastActivity: lastActivity.current, editing: isEditing.current, somethingOpen: isSomethingOpen() });

        void (async () => {
            // The page started: a release on trial is kept (AU-26).
            await bridge.app.ready().catch(() => undefined);
            const status = await bridge.app.updateStatus().catch(() => undefined);
            if (status) {
                const notice = updatedNotice(status, readSeen());
                if (notice) {
                    notify(notice);
                }
                writeSeen(status.active.version);
            }
        })();

        const timer = setInterval(() => void applyWhen('a quiet moment', quiet), STATUS_EVERY_MS);
        // Back in the foreground: someone is about to look, and nothing they did is on the page yet.
        const offForeground = bridge.lifecycle.onForeground(() => void applyWhen('back in the foreground', () => !isEditing.current && !isSomethingOpen()));

        return () => {
            clearInterval(timer);
            offForeground();
            window.removeEventListener('pointerdown', touched, { capture: true });
            window.removeEventListener('keydown', touched, { capture: true });
        };
    }, [bridge, notify]);
}
